import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import {
  RouterIcon,
  GlobeIcon,
  UserCircle,
  AtSign,
  Hash,
  Clock,
} from 'lucide-react';
import GenericDeviceCard, {
  type CardInfoField,
} from '@/components/GenericDeviceCard';
import { getRouterColor } from '@/utils/brandColors';
import CardPagination from '@/components/CardPagination';
import { ResultCardSkeleton } from '../styles';

interface ResultsGridProps {
  loading: boolean;
  data: DeviceType[];
  // Devices matching the search, across every page
  totalCount: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  onPageSizeChange: (size: number) => void;
  handleCardClick: (id: number) => void;
}

/** Displays equipments researched on grid mode */
const ResultsGrid: React.FC<ResultsGridProps> = ({
  loading,
  data,
  totalCount,
  page,
  setPage,
  onPageSizeChange,
  handleCardClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <CardPagination
      data={data}
      loading={loading}
      renderSkeleton={(index) => (
        <ResultCardSkeleton key={index} variant="rounded" />
      )}
      page={page}
      onPageChange={setPage}
      totalCount={totalCount}
      onPageSizeChange={onPageSizeChange}
      targetRows={3}
      renderItem={(device) => {
        const custData = device.customerData as CustomerDataType | undefined;

        const fields: CardInfoField[] = [
          {
            key: 'productClass',
            label: t('listEquipments.table.productClass'),
            value: device.model?.alias || device.productClass || '-',
            icon: <RouterIcon size={16} />,
          },
          {
            key: 'customerName',
            label: t('listEquipments.table.customerName'),
            value:
              custData?.customerName ||
              t('listEquipments.table.unknown').toUpperCase(),
            icon: <UserCircle size={16} />,
          },
          {
            key: 'userPPP',
            label: t('listEquipments.table.userPPP'),
            value: custData?.userPPP || '-',
            icon: <AtSign size={16} />,
          },
          {
            key: 'serialNumber',
            label: t('listEquipments.table.serialNumber'),
            value: device.serialNumber || '-',
            icon: <Hash size={16} />,
          },
          {
            key: 'ipAddress',
            label: t('listEquipments.table.ipAddress'),
            value: device.ipAddress || '-',
            icon: <GlobeIcon size={16} />,
          },
          {
            key: 'lastInform',
            label: t('listEquipments.table.lastInform'),
            value: device.lastInform
              ? new Date(device.lastInform.toString()).toLocaleString('pt-BR')
              : '-',
            icon: <Clock size={16} />,
          },
        ];

        return (
          <GenericDeviceCard
            key={device.id}
            onClick={() => handleCardClick(device.id)}
            chips={[
              {
                label: device.online
                  ? t('listEquipments.filter.online').toUpperCase()
                  : t('listEquipments.filter.offline').toUpperCase(),
                bgColor: device.online
                  ? theme.palette.primary.main
                  : theme.palette.grey['01'],
                textColor: device.online
                  ? theme.palette.primary.contrastText
                  : theme.palette.fixed.white,
              },
              ...(device.manufacturer
                ? [
                    {
                      label: device.manufacturer,
                      bgColor: getRouterColor(device.manufacturer),
                      textColor: theme.palette.getContrastText(
                        getRouterColor(device.manufacturer)
                      ),
                    },
                  ]
                : []),
            ]}
            fields={fields}
          />
        );
      }}
    />
  );
};

export default ResultsGrid;
